// Claude: Shared asset preview utilities — extracted from Feeds.tsx for reuse in Discover page 10-03-2026
// Claude: added AssetSummaryModal — shown when buyer clicks asset that has a summary 25-03-2026
// Extracted from Feeds.tsx — shared with Discover page
import { useEffect, useState } from "react";
import { SellerAPI } from "../Apis/sellerAPI";
import {
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import {
  MdPictureAsPdf,
  MdLanguage,
} from "react-icons/md";
// Claude: Import react-pdf for rendering PDF page 1 thumbnail in non-datasheet cards 09-02-2026
import { Document, Page, pdfjs } from "react-pdf";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Claude: asset item interface — used in Feeds and Discover 10-03-2026
export interface AssetItem {
  _id: string;
  assetTitle: string;
  assetUrl: string;
  updatedAt: string;
  assetSummary?: string;
  assetType?: string;
  coverImage?: string; // Claude: optional cover image URL — shown in feed instead of OG proxy 05-05-2026
}

// Kept for backward compat with Feeds.tsx
export type FeedsInterface = AssetItem;

//Claude 2026-02-08 OG metadata shape returned by our backend proxy endpoint
interface OgData {
  title?: string;
  description?: string;
  image?: string;
  logo?: string;
}

export type AssetType = "image" | "video" | "pdf" | "link";

// Extracted from Feeds.tsx — shared with Discover page
//Claude 2026-02-08 Detect asset type from URL extension/domain for preview rendering
export const getAssetType = (url: string): AssetType => {
  const lower = url.toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/.test(lower)) return "image";
  if (/youtube\.com|youtu\.be|vimeo\.com/.test(lower)) return "video";
  if (/\.pdf(\?.*)?$/.test(lower)) return "pdf";
  return "link";
};

// Extracted from Feeds.tsx — shared with Discover page
//Claude 2026-02-08 Convert YouTube/Vimeo watch URLs to embeddable iframe URLs
export const getEmbedUrl = (url: string): string => {
  // Claude: added shorts support 22-05-2026
  const ytMatch = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]+)/
  );
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;

  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  return url;
};

// Extracted from Feeds.tsx — shared with Discover page
//Claude 2026-02-08 LinkPreview sub-component for "link" type assets.
// Claude: coverImage prop — if provided, skips OG proxy and renders it directly 05-05-2026
export const LinkPreview = ({ url, title, coverImage }: { url: string; title: string; coverImage?: string }) => {
  const [og, setOg] = useState<OgData | null>(coverImage ? { image: coverImage } : null);
  const [loading, setLoading] = useState(!coverImage);

  useEffect(() => {
    if (coverImage) return;
    (SellerAPI as any)
      .getOgPreview(url)
      .then((data: OgData) => {
        setOg(data);
        setLoading(false);
      })
      .catch(() => {
        setOg({});
        setLoading(false);
      });
  }, [url, coverImage]);

  if (loading) {
    return (
      <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
        <Spinner size="lg" color="secondary.main" />
      </Flex>
    );
  }

  const hasImage = !!og?.image;
  let hostname = "";
  try {
    hostname = new URL(url).hostname.replace("www.", "");
  } catch {
    hostname = url;
  }

  return (
    <Flex direction="column" w="100%" h="100%">
      {hasImage ? (
        <Image
          src={og!.image}
          alt={og?.title || title}
          w="100%"
          flex="1"
          objectFit="cover"
          minH="0"
        />
      ) : (
        <Flex
          flex="1"
          alignItems="center"
          justifyContent="center"
          bg="linear-gradient(135deg, #0E122F 0%, #1a2150 100%)"
          minH="0"
        >
          {og?.logo ? (
            <Image
              src={og.logo}
              alt="site logo"
              boxSize="80px"
              borderRadius="16px"
              bg="white"
              p={2}
              shadow="lg"
            />
          ) : (
            <MdLanguage size={80} color="rgba(255,255,255,0.5)" />
          )}
        </Flex>
      )}
      <Box px={3} py={2.5} bg="gray.50">
        {og?.description && (
          <Text fontSize="sm" color="gray.600" noOfLines={2} mb={1}>
            {og.description}
          </Text>
        )}
        <Flex alignItems="center" gap={1.5}>
          <MdLanguage size={13} color="#718096" />
          <Text fontSize="xs" color="gray.400" noOfLines={1}>
            {hostname}
          </Text>
        </Flex>
      </Box>
    </Flex>
  );
};

// Claude: PdfPreview — datasheet (name-value pairs) or page 1 thumbnail for general PDFs 09-02-2026
// Extracted from Feeds.tsx — shared with Discover page
export const PdfPreview = ({ summary, url }: { summary: string; url: string }) => {
  let contentType: "datasheet" | "preview" | "text" = "text";
  let pairs: { name: string; value: string }[] = [];

  try {
    const parsed = JSON.parse(summary);
    if (parsed?.type === "datasheet" && Array.isArray(parsed.data)) {
      contentType = "datasheet";
      pairs = parsed.data;
    } else if (parsed?.type === "preview") {
      contentType = "preview";
    }
  } catch {
    // not JSON, render as plain text
  }

  return (
    <Flex direction="column" w="100%" h="100%" p={contentType === "preview" ? 0 : 4} overflow="hidden">
      {contentType === "datasheet" && (
        <Flex mb={3} flexShrink={0}>
          <MdPictureAsPdf size={28} color="#E53E3E" />
        </Flex>
      )}
      <Box flex="1" overflowY="auto" minH="0">
        {contentType === "datasheet" ? (
          pairs.map((item, i) => (
            <Flex key={i} mb={1.5} fontSize="sm" lineHeight="1.4">
              <Text fontWeight={600} color="gray.700" mr={1} flexShrink={0}>
                {item.name}:
              </Text>
              <Text color="gray.600">{item.value}</Text>
            </Flex>
          ))
        ) : contentType === "preview" ? (
          <Document
            file={`http://localhost:9000/sellers/assets/pdf-proxy?url=${encodeURIComponent(url)}`}
            loading={<Spinner size="lg" color="red.400" />}
            error={<Text fontSize="sm" color="gray.500">Could not load preview</Text>}
          >
            <Page pageNumber={1} renderTextLayer={false} renderAnnotationLayer={false} />
          </Document>
        ) : (
          <Text fontSize="sm" color="gray.600" whiteSpace="pre-wrap" noOfLines={20}>
            {summary}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

// Claude: AssetSummaryModal — shown when buyer clicks an asset that has a manual summary 25-03-2026
// If no summary → caller should open URL directly (see openAssetOrModal helper below)
export const AssetSummaryModal = ({
  isOpen, onClose, asset,
}: {
  isOpen: boolean;
  onClose: () => void;
  asset: AssetItem | null;
}) => {
  if (!asset) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="8px">
        <ModalHeader fontSize="md" fontWeight={700} pr={10}>{asset.assetTitle}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">{asset.assetSummary}</Text>
        </ModalBody>
        <ModalFooter>
          <Button
            bg="#0E122F" color="white" _hover={{ bg: "#2E7D32" }} size="sm"
            onClick={() => { window.open(asset.assetUrl, "_blank", "noopener,noreferrer"); onClose(); }}
          >
            View More
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Claude: helper — if asset has a summary show the modal; otherwise open URL directly 25-03-2026
export const openAssetOrModal = (
  asset: AssetItem,
  openModal: (asset: AssetItem) => void,
) => {
  if (asset.assetSummary?.trim()) {
    openModal(asset);
  } else {
    window.open(asset.assetUrl, "_blank", "noopener,noreferrer");
  }
};
